import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card as CardType } from '../../api/client';
import { Tag } from '../common/Tag';
import { useBoardStore } from '../../stores/boardStore';
import { useContextMenu } from '../../hooks/useContextMenu';
import { useLongPress } from '../../hooks/useLongPress';
import { CardContextMenu } from './CardContextMenu';

interface CardProps {
  card: CardType;
}

export function Card({ card }: CardProps) {
  const { selectCard } = useBoardStore();
  const { menu, showMenu, hideMenu } = useContextMenu();
  const [isPressing, setIsPressing] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id,
    data: {
      type: 'card',
      card,
    },
  });

  const longPressProps = useLongPress(
    (e) => { setIsPressing(false); showMenu(e); },
    undefined,
    { delay: 1000 }
  );

  const wrappedLongPress = {
    ...longPressProps,
    onPointerDown: (e: React.PointerEvent) => {
      setIsPressing(true);
      longPressProps.onPointerDown(e);
    },
    onPointerUp: (e: React.PointerEvent) => {
      setIsPressing(false);
      longPressProps.onPointerUp(e);
    },
    onPointerLeave: (e: React.PointerEvent) => {
      setIsPressing(false);
      longPressProps.onPointerLeave(e);
    },
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    showMenu(e);
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();

    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return date.toLocaleDateString();
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        data-card-id={card.id}
        {...attributes}
        {...wrappedLongPress}
        {...listeners}
        onContextMenu={handleContextMenu}
        className={`bg-white/92 rounded-2xl border border-slate-200/90 p-4 cursor-grab active:cursor-grabbing hover:border-slate-300 hover:shadow-[0_16px_30px_rgba(15,76,129,0.10)] transition-all duration-200 relative group data-[card-focused=true]:ring-2 data-[card-focused=true]:ring-blue-400 data-[card-focused=true]:border-blue-300 ${
          isDragging ? 'opacity-50 shadow-2xl scale-105 rotate-2 z-50' :
          isPressing ? 'scale-[0.97] shadow-lg border-blue-300 z-10' :
          'hover:-translate-y-1 z-0'
        }`}
      >
        {card.color_label && (
          <div
            className="h-2 -mx-4 -mt-4 mb-3 rounded-t-2xl shadow-sm"
            style={{ backgroundColor: card.color_label }}
          />
        )}

        <div className="mb-3 flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
            <span className="h-2 w-2 rounded-full bg-slate-300" />
            Updated {formatTime(card.updated_at)}
          </div>

          <button
            onClick={(e) => {
              e.stopPropagation();
              selectCard(card.id);
            }}
            className="rounded-lg border border-transparent bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 opacity-0 transition-all hover:border-slate-200 hover:bg-white hover:text-slate-900 group-hover:opacity-100"
          >
            Open
          </button>
        </div>

        <h3 className="font-semibold text-slate-900 text-[15px] mb-2 line-clamp-2 leading-snug group-hover:text-blue-700 transition-colors">
          {card.title}
        </h3>

        {card.description && (
          <p className="text-sm text-slate-600 mb-3 line-clamp-3 leading-relaxed">
            {card.description}
          </p>
        )}

        {card.word_count > 0 && (
          <div className="flex items-center gap-2 mb-3 text-[11px] text-slate-400">
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{card.word_count.toLocaleString()} words</span>
            <span className="text-slate-300">·</span>
            <span>{Math.max(1, Math.round(card.word_count / 238))} min read</span>
          </div>
        )}

        <div className="flex items-end justify-between gap-3 mt-3">
          <div className="flex flex-wrap gap-1.5 max-w-[72%]">
            {card.tags.slice(0, 2).map((tag) => (
              <Tag key={tag.id} name={tag.name} color={tag.color} size="sm" />
            ))}
            {card.tags.length > 2 && (
              <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-[10px] font-semibold text-slate-500">
                +{card.tags.length - 2}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 text-slate-400 flex-shrink-0">
            {card.comment_count > 0 && (
              <div className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-500">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                {card.comment_count}
              </div>
            )}

            <div className="opacity-50 group-hover:opacity-100 transition-opacity">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {menu.visible && (
        <CardContextMenu
          card={card}
          x={menu.x}
          y={menu.y}
          onClose={hideMenu}
        />
      )}
    </>
  );
}
